import Image from 'components/shared/image/image';
import useIsMobile from 'hooks/utils/useIsMobile';
import classNames from 'classnames';
import SiteLink from 'components/shared/sitelink/sitelink';
import React, { useMemo, useState, useEffect } from 'react';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import WishlistButton from 'components/shared/wishlist-button';
import UnitPrice from 'components/shared/unit-price/unitPrice';
import { formatPriceByStoreLocale } from 'lib/shopify';
import getRatings from 'lib/reviews/getRatings';
import groupRating from 'lib/reviews/groupRating';
import { useGlobalModules } from 'contexts';
import ProductLabels from 'components/shared/productLabels/productLabels';
import styles from './productCard.module.scss';
import QuickCartSection from '../quickCartSection';
import RatingStars from '../ratingStars';

const ProductCard = ({
  className,
  locale,
  onClick,
  itemData,
  itemIndex,
  pageTitle,
  attributeLabelTextColor,
  attributeLabelBackgroundColor,
  freeItemLabel,
  hideQuickCart,
}) => {
  const { title, type, slug, productAttributes, size, customLabel, cardShopifyData, rating } =
    itemData;

  const globalModules = useGlobalModules();

  const { hasWebshop } = globalModules.settings.locale;

  // if not ratings try to fetch the ratings using getRatings
  const [productRating, setProductRating] = useState(rating);

  useEffect(() => {
    const sku = itemData?.shopifyData?.variants?.[0]?.sku;
    if (!rating && locale && sku) {
      const fetchRatings = async () => {
        const ratings = await getRatings({
          skuArr: [sku],
          locale,
        });

        const group = groupRating(ratings, sku);
        if (group) setProductRating({ averageRating: group.average, totalReviews: group.total });
      };
      fetchRatings();
    }
  }, [rating, itemData, locale]);

  const productPricesData = useMemo(
    () => ({
      price: cardShopifyData?.productPrice,
      compareAtPrice: cardShopifyData?.productCompareAtPrice,
      salePercentage: cardShopifyData?.salePercentage,
      outOfStock: cardShopifyData?.outOfStock,
    }),
    [cardShopifyData]
  );

  const { price, compareAtPrice, outOfStock } = productPricesData;

  const unitPrice = Array.isArray(cardShopifyData)
    ? itemData.shopifyData?.variants?.[0]?.unitPrice
    : cardShopifyData?.unitPrice;

  const isMobile = useIsMobile();
  const router = useRouter();
  const renderCorrectImage = collectionImage => {
    if (isMobile) return collectionImage.mobile;
    return collectionImage.desktop;
  };

  const propImage = type === 'PageGeneral' ? itemData?.highlightsImage : itemData?.image;
  const [isHovered, setIsHovered] = useState(false);

  const handleImage = (pageType, item) => {
    if (pageType === 'PageGeneral') {
      return item?.highlightsImage;
    }
    if (pageType === 'PageCollection') {
      return renderCorrectImage(item?.image);
    }
    return item?.image;
  };

  const concatProductPathWithSlug = () =>
    addLocaleToHref(
      `${type === 'PageProduct' || type === 'PageProductBundle' ? 'p/' : ''}${slug}`,
      locale,
      router
    );

  const productHref = concatProductPathWithSlug();

  return (
    <>
      {propImage && (
        <div className={classNames(styles.container, className)}>
          <SiteLink
            locale={locale}
            draggable={false}
            href={productHref}
            className={styles.cardLink}
            onClick={onClick}
          >
            <div
              className={classNames(styles.imageWrapper, {
                [styles.productAnimation]: isHovered,
              })}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Image
                className={styles.image}
                src={handleImage(
                  type,
                  isHovered && itemData?.productPictures?.[0]
                    ? { image: itemData.productPictures[0] }
                    : itemData
                )}
                alt=""
                draggable={false}
                sizes="50vw"
                layout="fill"
              />
              <ProductLabels
                freeItemLabel={freeItemLabel}
                customLabel={customLabel}
                productAttributes={productAttributes}
                attributeLabelTextColor={attributeLabelTextColor}
                attributeLabelBackgroundColor={attributeLabelBackgroundColor}
              />
              {productRating?.averageRating > 0 && (
                <RatingStars
                  className={styles.rating}
                  rating={productRating?.averageRating}
                  max={5}
                  starSize="1.4rem"
                  starSizeMobile="1.2rem"
                />
              )}
            </div>
            <div className={styles.productInfo}>
              <h3 className={styles.productTitle}>{title}</h3>
              {hasWebshop && (
                <span className={styles.productPrices}>
                  <span>
                    {compareAtPrice ? (
                      <s>
                        {formatPriceByStoreLocale({
                          locale,
                          price: compareAtPrice,
                        })}
                      </s>
                    ) : (
                      formatPriceByStoreLocale({
                        locale,
                        price,
                      })
                    )}
                    {compareAtPrice && (
                      <span className={styles.salesPrice}>
                        {formatPriceByStoreLocale({
                          locale,
                          price,
                        })}
                      </span>
                    )}
                  </span>
                  <UnitPrice size={size} price={price} unitPrice={unitPrice} locale={locale} />
                </span>
              )}
            </div>
          </SiteLink>
          {hasWebshop && (
            <div className={styles.imageOverlay}>
              <WishlistButton
                className={styles.wishlistIcon}
                productData={itemData}
                list={`Category: ${pageTitle}`}
                index={itemIndex}
              />
            </div>
          )}
          {hasWebshop && !hideQuickCart && (
            <QuickCartSection
              productData={itemData}
              itemIndex={itemIndex}
              pageTitle={pageTitle}
              locale={locale}
              outOfStock={outOfStock}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ProductCard;
